import { useState, useEffect, useCallback } from "react";

import { motion } from "motion/react";

import { Check, X } from "lucide-react";

import {
  useGetChatWithCandidate,
  useSendMessageToCandidate,
} from "@/entities/recruit-chat/api";

import { Roles } from "@/entities/user";

import { Modal } from "@/shared/ui/Modal";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { formatDate } from "@/shared/lib/utils";
import { Label } from "@/shared/ui/label";
import type { Survey } from "@/entities/candidate/model";

interface ChatModalProps {
  candidateId: string;
  candidateName: string;
  role: Roles.Moderator | Roles.Head;
  survey: Survey;
}

type Message = {
  text: string;
  sender: "moderator" | "candidate";
  date: Date;
};

export const ChatModal = ({
  candidateName,
  role,
  candidateId,
  survey,
}: ChatModalProps) => {
  const {
    data: chatWithCandidate,
    isPending,
    isError,
    refetch,
  } = useGetChatWithCandidate({
    candidateId,
  });

  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState<string>("");

  const [chatId, setChatId] = useState<string>("");

  const { mutateAsync: sendMessage } = useSendMessageToCandidate({
    chatId,
    candidateId,
  });

  useEffect(() => {
    if (chatWithCandidate?.data) {
      setMessages(chatWithCandidate.data.messages);
      setChatId(chatWithCandidate.data._id);
    }
  }, [chatWithCandidate]);

  const handleSendMessage = useCallback(() => {
    if (newMessage.trim() === "") return;

    const newMsg: Message = {
      text: newMessage,
      sender: "moderator",
      date: new Date(),
    };
    setMessages((prevMessages) => [...prevMessages, newMsg]);
    setNewMessage("");

    sendMessage({ message: newMsg.text }, { onSuccess: () => refetch() });
  }, [newMessage, sendMessage, refetch]);

  return (
    <Modal>
      <Modal.Trigger>Перейти в чат</Modal.Trigger>
      <Modal.Content className="min-h-[500px] min-w-[50px]">
        <Modal.Title>Чат с {candidateName}</Modal.Title>
        <div className="flex flex-col space-y-4">
          <div className="flex flex-1 flex-col justify-end space-y-2 overflow-y-auto">
            {isPending ? (
              <div>Загрузка...</div>
            ) : isError ? (
              <div>Ошибка загрузки сообщений</div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col gap-2"
              >
                {messages.map((message, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className={`max-w-96 rounded p-2 ${
                      message.sender === "moderator"
                        ? "self-end bg-blue-200"
                        : "self-start bg-gray-200"
                    }`}
                  >
                    <div className="whitespace-pre-wrap break-words text-sm">
                      {message.text}
                    </div>
                    <div className="pt-2 text-xs text-gray-500">
                      {formatDate(new Date(message.date))}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>

          {role === Roles.Moderator && !chatWithCandidate?.data.closed ? (
            <div className="flex items-center space-x-2">
              <div className="flex-1 flex-grow">
                <Input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Введите сообщение..."
                  className="rounded border p-2"
                />
              </div>
              <Button
                disabled={newMessage.length === 0}
                onClick={handleSendMessage}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSendMessage();
                }}
                className="h-full w-24"
              >
                Отправить
              </Button>
            </div>
          ) : (
            <span className="text-center font-bold text-textColor">
              Заявка рассмотрена
            </span>
          )}
          <div className="flex flex-col gap-2">
            <Label className="text-xl">Опрос</Label>
            <div className="flex flex-row items-center justify-between">
              <Label>Опыт в привлечении трафика:</Label>
              {survey?.hasTrafficSearchExperience ? (
                <Check className="text-textColor" />
              ) : (
                <X className="text-textColor" />
              )}
            </div>
            <div className="flex flex-row items-center justify-between">
              <Label>Подробно о опыте:</Label>
              <Label>{survey?.trafficSearchExperienceDetails}</Label>
            </div>
            <div className="flex flex-row items-center justify-between">
              <Label>Опыт в обработке трафика:</Label>
              <Label>
                {survey?.hasTrafficProcessingExperience ? (
                  <Check className="text-textColor" />
                ) : (
                  <X className="text-textColor" />
                )}
              </Label>
            </div>
            <div className="flex flex-row items-center justify-between">
              <Label>Время для работы:</Label>
              <Label>{survey?.dedicatedTimeForJob}</Label>
            </div>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
};
